import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Citerne, CiterneConsommationRaw, CiterneConsommationAgg } from '@/types';
import api from '@/api/client';
import { parsePaginatedResponse } from '@/api/types';
import { setCiternes, editCiterne, removeCiterne, setNotAffectedCiternes, setConsommations } from '../slices/citerneSlice';
import { setToast } from '../slices/toastSlice';

const PAGE_SIZE = 10;

export const fetchCiternes = createAsyncThunk<void, number | undefined>(
  'citerne/fetch',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/citernes/', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<Citerne>(data);
        dispatch(setCiternes({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: Citerne[] }>('/citernes/');
        dispatch(setCiternes(data.data ?? []));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur chargement citernes';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const createCiterne = createAsyncThunk<boolean, Partial<Citerne>, { rejectValue: string }>(
  'citerne/create',
  async (citerne, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/citernes/', citerne);
      await dispatch(fetchCiternes(0));
      dispatch(setToast({ message: 'Citerne créée', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur création';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateCiterne = createAsyncThunk<boolean, Citerne, { rejectValue: string }>(
  'citerne/update',
  async (citerne, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/citernes/${citerne.id}`, citerne);
      dispatch(editCiterne(citerne));
      dispatch(setToast({ message: 'Citerne modifiée', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchNotAffectedCiternes = createAsyncThunk(
  'citerne/fetchNotAffected',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get<{ data: Citerne[] }>('/citernes/not_affected');
      dispatch(setNotAffectedCiternes(data.data ?? []));
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

function aggregateConsommations(raw: CiterneConsommationRaw[]): CiterneConsommationAgg[] {
  const currentDate = new Date();
  const byCiterne = new Map<number, { citerne: Citerne; consomation_moy: number; consomation_moy_count: number; consomation_j: number; prix: number }>();
  for (const row of raw) {
    const id = row.citerne?.id;
    if (id == null) continue;
    const dateSortie = new Date(row.date);
    const existing = byCiterne.get(id);
    if (existing) {
      existing.consomation_moy += row.sum ?? 0;
      existing.consomation_moy_count += 1;
      if (currentDate.getDate() === dateSortie.getDate() && currentDate.getMonth() === dateSortie.getMonth() && currentDate.getFullYear() === dateSortie.getFullYear()) {
        existing.consomation_j = (row.count ? (row.sum ?? 0) / row.count : 0);
      }
    } else {
      byCiterne.set(id, {
        citerne: row.citerne,
        consomation_moy: row.sum ?? 0,
        consomation_moy_count: 1,
        consomation_j: (currentDate.getDate() === dateSortie.getDate() && row.count) ? (row.sum ?? 0) / row.count : 0,
        prix: row.prix ?? 0,
      });
    }
  }
  return Array.from(byCiterne.values()).map((x) => ({
    citerne: x.citerne,
    consomation_moy: x.consomation_moy,
    consomation_moy_count: x.consomation_moy_count,
    consomation_j: x.consomation_j,
    prix: x.prix,
  }));
}

export const fetchCiterneConsommations = createAsyncThunk<void, void, { rejectValue: string }>(
  'citerne/fetchConsommations',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get<{ data: CiterneConsommationRaw[] }>('/citernes/consommations');
      const list = Array.isArray(data?.data) ? data.data : [];
      dispatch(setConsommations(aggregateConsommations(list)));
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

export const deleteCiterne = createAsyncThunk<boolean, Citerne, { rejectValue: string }>(
  'citerne/delete',
  async (citerne, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/citernes/${citerne.id}`);
      dispatch(removeCiterne(citerne));
      dispatch(setToast({ message: 'Citerne supprimée', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur suppression';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);
